import React, { createContext, useContext, useState } from "react";

type ReportContextType = {
  isReportOpen: boolean;
  setIsReportOpen(open: boolean): void;
};

const ReportContext = createContext<ReportContextType | undefined>(undefined);

/**
 * Hook to access the report context. Must be used within a ReportProvider.
 *
 * @returns {ReportContextType} The context value.
 */
export function useRegistrationReportContext(): ReportContextType {
  const context = useContext(ReportContext);
  if (!context) {
    throw new Error(
      "useRegistrationReportContext must be used within ReportProvider",
    );
  }
  return context;
}

/**
 * Provides the report context to its children.
 *
 * @returns {React.ReactElement} The provider component.
 */
export function ReportProvider({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  const [isReportOpen, setIsReportOpen] = useState(false);
  const value = React.useMemo(
    () => ({ isReportOpen, setIsReportOpen }),
    [isReportOpen, setIsReportOpen],
  );
  return <ReportContext value={value}>{children}</ReportContext>;
}
